import React, { useState, useMemo, useCallback } from 'react';
import type { WarehouseZone, WarehouseRack, WarehouseBin } from '../../types/warehouse';
import {
  Search,
  X,
  Layers,
  Grid3X3,
  Box,
  MapPin,
  ChevronRight,
} from 'lucide-react';

interface MapSearchPanelProps {
  zones: WarehouseZone[];
  racks: WarehouseRack[];
  bins: WarehouseBin[];
  onSelectZone: (zoneId: string) => void;
  onSelectRack: (rackId: string) => void;
  onCenterOnZone: (zone: WarehouseZone) => void;
  onCenterOnRack: (rack: WarehouseRack) => void;
  onClose: () => void;
}

interface SearchResult {
  type: 'zone' | 'rack' | 'bin';
  id: string;
  code: string;
  name?: string;
  parentInfo?: string;
  zone?: WarehouseZone;
  rack?: WarehouseRack;
  bin?: WarehouseBin;
}

export const MapSearchPanel: React.FC<MapSearchPanelProps> = ({
  zones,
  racks,
  bins,
  onSelectZone,
  onSelectRack,
  onCenterOnZone,
  onCenterOnRack,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const searchResults = useMemo((): SearchResult[] => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase().trim();
    const results: SearchResult[] = [];

    for (const zone of zones) {
      if (
        zone.zone_code.toLowerCase().includes(query) ||
        zone.zone_name?.toLowerCase().includes(query)
      ) {
        results.push({
          type: 'zone',
          id: zone.id,
          code: zone.zone_code,
          name: zone.zone_name || undefined,
          zone,
        });
      }
    }

    for (const rack of racks) {
      const zone = zones.find((z) => z.id === rack.zone_id);
      if (
        rack.rack_code.toLowerCase().includes(query) ||
        rack.rack_name?.toLowerCase().includes(query)
      ) {
        results.push({
          type: 'rack',
          id: rack.id,
          code: rack.rack_code,
          name: rack.rack_name || undefined,
          parentInfo: zone ? `Zone ${zone.zone_code}` : 'Unassigned',
          rack,
        });
      }
    }

    for (const bin of bins) {
      if (bin.bin_code.toLowerCase().includes(query)) {
        const rack = racks.find((r) => r.id === bin.rack_id);
        const zone = rack?.zone_id ? zones.find((z) => z.id === rack.zone_id) : null;
        results.push({
          type: 'bin',
          id: bin.id,
          code: bin.bin_code,
          parentInfo: rack
            ? `${rack.rack_code}${zone ? ` in ${zone.zone_code}` : ''}`
            : undefined,
          bin,
          rack: rack || undefined,
        });
      }
    }

    return results.slice(0, 20);
  }, [searchQuery, zones, racks, bins]);

  const handleResultClick = useCallback(
    (result: SearchResult) => {
      if (result.type === 'zone' && result.zone) {
        onSelectZone(result.zone.id);
        onCenterOnZone(result.zone);
      } else if (result.type === 'rack' && result.rack) {
        onSelectRack(result.rack.id);
        onCenterOnRack(result.rack);
      } else if (result.type === 'bin' && result.rack) {
        onSelectRack(result.rack.id);
        onCenterOnRack(result.rack);
      }
    },
    [onSelectZone, onSelectRack, onCenterOnZone, onCenterOnRack]
  );

  const getResultIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'zone':
        return Layers;
      case 'rack':
        return Grid3X3;
      case 'bin':
        return Box;
      default:
        return MapPin;
    }
  };

  const getResultColor = (type: SearchResult['type'], zone?: WarehouseZone) => {
    if (type === 'zone' && zone) {
      return zone.color;
    }
    switch (type) {
      case 'zone':
        return '#3B82F6';
      case 'rack':
        return '#10B981';
      case 'bin':
        return '#F59E0B';
      default:
        return '#6B7280';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden w-72">
      <div className="p-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Search className="h-4 w-4 text-blue-500" />
            Search Map
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search zones, racks, bins..."
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-400"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="max-h-80 overflow-y-auto">
        {searchQuery.trim() === '' ? (
          <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
            Type to search for zones, racks, or bins
          </div>
        ) : searchResults.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
            No results found for "{searchQuery}"
          </div>
        ) : (
          <div className="py-1">
            {searchResults.map((result) => {
              const Icon = getResultIcon(result.type);
              const color = getResultColor(result.type, result.zone);

              return (
                <button
                  key={`${result.type}-${result.id}`}
                  onClick={() => handleResultClick(result)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left transition-colors"
                >
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${color}20` }}
                  >
                    <Icon className="h-4 w-4" style={{ color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                        {result.code}
                      </span>
                      <span className="text-[10px] uppercase tracking-wider text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                        {result.type}
                      </span>
                    </div>
                    {(result.name || result.parentInfo) && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {result.name || result.parentInfo}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="p-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>{searchResults.length} results</span>
          <span className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-blue-500" />
              Zones
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              Racks
            </span>
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              Bins
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};
